import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface InlineTextFieldProps {
  value: string | null;
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  testId?: string;
  /** Optional function to format the display value (e.g., currency formatting or JSX links) */
  formatDisplay?: (value: string) => React.ReactNode;
}

type SaveState = "idle" | "saving" | "success" | "error";

export function InlineTextField({
  value,
  onSave,
  placeholder = "Enter value...",
  className,
  disabled = false,
  label,
  testId,
  formatDisplay,
}: InlineTextFieldProps) {
  const [localValue, setLocalValue] = useState(value || "");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleBlur = async () => {
    setIsEditing(false);
    
    if (localValue === (value || "")) {
      return;
    }

    setSaveState("saving");

    try {
      await onSave(localValue);
      setSaveState("success");
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 2000);
    } catch {
      setSaveState("error");
      setLocalValue(value || "");
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 3000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      setLocalValue(value || "");
      setIsEditing(false);
    }
  };

  const SaveIndicator = () => {
    if (saveState === "saving") {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    if (saveState === "success") {
      return <Check className="h-4 w-4 text-green-600 dark:text-green-400" />;
    }
    if (saveState === "error") {
      return <X className="h-4 w-4 text-destructive" />;
    }
    return null;
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="text-sm font-medium text-muted-foreground">
          {label}
        </label>
      )}
      <div className="relative flex items-center gap-2">
        {!isEditing && !disabled ? (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className={cn(
              "flex-1 text-left px-2 py-1 rounded-md border border-transparent hover-elevate active-elevate-2 transition-colors",
              !localValue && "text-muted-foreground",
              className
            )}
            data-testid={testId ? `${testId}-display` : undefined}
          >
            {localValue ? (formatDisplay ? formatDisplay(localValue) : localValue) : placeholder}
          </button>
        ) : (
          <Input
            ref={inputRef}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || saveState === "saving"}
            className={cn("flex-1", className)}
            data-testid={testId ? `${testId}-input` : undefined}
          />
        )}
        <div className="w-4 flex items-center justify-center">
          <SaveIndicator />
        </div>
      </div>
    </div>
  );
}
